import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { generateThumbnail } from '../utils/thumbnailGenerator';

// 全局缩略图缓存
const thumbnailCacheGlobal: Record<string, string> = {};

// 懒加载缩略图组件 - 提取到 AdminPanel 外部避免重复挂载
const LazyThumbnail: React.FC<{ videoUrl: string; videoId: string; serverThumbnailUrl?: string | null }> = 
  ({ videoUrl, videoId, serverThumbnailUrl }) => {
  const [thumbnail, setThumbnail] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(true);
  const [isVisible, setIsVisible] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: '150px' }
    );
    
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  
  useEffect(() => {
    if (!isVisible) return;
    
    if (serverThumbnailUrl) {
      setThumbnail(serverThumbnailUrl);
      setLoading(false);
      return;
    }
    
    if (thumbnailCacheGlobal[videoId]) {
      setThumbnail(thumbnailCacheGlobal[videoId]);
      setLoading(false);
      return;
    }
    
    generateThumbnail(videoUrl)
      .then(thumb => {
        thumbnailCacheGlobal[videoId] = thumb;
        setThumbnail(thumb);
        setLoading(false);
      })
      .catch(error => {
        console.error('生成缩略图失败:', error);
        setThumbnail('');
        setLoading(false);
      });
  }, [isVisible, videoUrl, videoId, serverThumbnailUrl]);
  
  if (!isVisible || loading) {
    return (
      <div ref={containerRef} className="w-full h-full flex items-center justify-center bg-gray-900">
        <div className="text-white text-lg">加载中...</div>
      </div>
    );
  }
  
  if (!thumbnail) {
    return (
      <div ref={containerRef} className="w-full h-full flex items-center justify-center bg-gradient-to-br from-purple-900 to-pink-900">
        <div className="text-white text-4xl">▶</div>
      </div>
    );
  }
  
  return (
    <div ref={containerRef} className="w-full h-full relative">
      <img src={thumbnail} alt="视频缩略图" className="w-full h-full object-cover" loading="lazy" />
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="text-white text-4xl">▶</div>
      </div>
    </div>
  );
};

interface VideoItem {
  id: string;
  filename: string;
  size: number;
  uploadDate: string;
  url: string;
  tags: string[];
  description: string;
  title?: string;
  group: string;
  viewCount?: number;
  likeCount?: number;
  isFavorite?: boolean;
  author?: string;
  authorId?: string;
  originalUrl?: string;
  thumbnailUrl?: string | null;
  hasThumbnail?: boolean;
  // 统计信息
  watchTimeStats?: {
    totalWatchTime: number;
    averageWatchTime: number;
    completionRate: number;
    totalViews: number;
    completedViews: number;
  };
}

// 统计仪表板数据接口
interface DashboardStats {
  totalVideos: number;
  totalSize: number;
  favoriteCount: number;
  avgCompletionRate: number;
  // 新增统计字段
  totalViews: number;
  totalWatchTime: number;
  avgViewDuration: number;
  topGroups: { name: string; count: number }[];
  topTags: { name: string; count: number }[];
  recentUploads: number;
  engagementRate: number;
}

const PAGE_SIZE = 12;

// 提取统计计算为纯函数，避免 hooks 顺序问题和重复创建
const calculateStatsPure = (videoList: VideoItem[]): DashboardStats => {
  const totalVideos = videoList.length;
  const totalSize = videoList.reduce((sum, video) => sum + video.size, 0);
  const favoriteCount = videoList.filter(video => video.isFavorite).length;
  
  const totalViews = videoList.reduce((sum, video) => sum + (video.viewCount || 0), 0);
  const totalWatchTime = videoList.reduce((sum, video) => sum + (video.watchTimeStats?.totalWatchTime || 0), 0);
  const avgViewDuration = totalViews > 0 ? Math.round(totalWatchTime / totalViews) : 0;
  
  const completionRates = videoList
    .map(video => video.watchTimeStats?.completionRate || 0)
    .filter(rate => rate > 0);
  const avgCompletionRate = completionRates.length > 0 
    ? Math.round(completionRates.reduce((sum, rate) => sum + rate, 0) / completionRates.length)
    : 0;
  
  const groupCounts: Record<string, number> = {};
  videoList.forEach(video => {
    groupCounts[video.group] = (groupCounts[video.group] || 0) + 1;
  });
  const topGroups = Object.entries(groupCounts)
    .map(([name, count]) => ({ name, count }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 5);
  
  const tagCounts: Record<string, number> = {};
  videoList.forEach(video => {
    video.tags.forEach(tag => {
      tagCounts[tag] = (tagCounts[tag] || 0) + 1;
    });
  });
  const topTags = Object.entries(tagCounts)
    .map(([name, count]) => ({ name, count }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 5);
  
  const sevenDaysAgo = new Date();
  sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
  const recentUploads = videoList.filter(video => 
    new Date(video.uploadDate) >= sevenDaysAgo
  ).length;
  
  const videosWithViews = videoList.filter(video => (video.viewCount || 0) > 0).length;
  const engagementRate = totalVideos > 0 ? Math.round((videosWithViews / totalVideos) * 100) : 0;
  
  return { 
    totalVideos, 
    totalSize, 
    favoriteCount, 
    avgCompletionRate,
    totalViews,
    totalWatchTime,
    avgViewDuration,
    topGroups,
    topTags,
    recentUploads,
    engagementRate
  };
};

const AdminPanel: React.FC = () => {
  const [videos, setVideos] = useState<VideoItem[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [editingVideo, setEditingVideo] = useState<VideoItem | null>(null);
  const [viewingVideo, setViewingVideo] = useState<VideoItem | null>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [showVideoList, setShowVideoList] = useState(false);
  
  // 编辑表单数据状态
  const [editFormData, setEditFormData] = useState<{
    title: string;
    description: string;
    tags: string;
    group: string;
  }>({
    title: '',
    description: '',
    tags: '',
    group: ''
  });
  
  // 新增：批量操作和搜索
  const [selectedVideos, setSelectedVideos] = useState<string[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterGroup, setFilterGroup] = useState('all');
  const [sortBy, setSortBy] = useState('uploadDate');
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showBatchEditModal, setShowBatchEditModal] = useState(false);
  const [batchEditType, setBatchEditType] = useState<'tags' | 'title' | 'description'>('tags');
  const [batchEditValue, setBatchEditValue] = useState('');
  const [showAutoGroupModal, setShowAutoGroupModal] = useState(false);
  const [autoGroupStrategy, setAutoGroupStrategy] = useState<'smart' | 'time' | 'source' | 'size'>('smart');
  const [isAutoGrouping, setIsAutoGrouping] = useState(false);
  
  // 批量导入状态
  const [showBatchImportModal, setShowBatchImportModal] = useState(false);
  const [importDir, setImportDir] = useState('');
  const [importMetadataMode, setImportMetadataMode] = useState<'separate' | 'together'>('separate');
  const [importDefaultGroup, setImportDefaultGroup] = useState('默认分组');
  const [importAutoTag, setImportAutoTag] = useState(true);
  const [importSkipDuplicates, setImportSkipDuplicates] = useState(false);
  const [importValidateVideos, setImportValidateVideos] = useState(false); // 新增：校验视频有效性
  const [isImporting, setIsImporting] = useState(false);
  const [importResult, setImportResult] = useState<any>(null);
  
  // 播放设置
  const [playbackSettings, setPlaybackSettings] = useState<{
    shuffle: boolean;
    group: string | string[]; // V6.1: 支持单个或多个分组
    recommend: boolean;
    coldVideoInsert: boolean;
    coldVideoInterval: number;
    maxColdVideos: number;
    loopPlayback: boolean;
    swipeSensitivity: number;
  }>({ shuffle: true, group: 'all', recommend: false, coldVideoInsert: false, coldVideoInterval: 5, maxColdVideos: 3, loopPlayback: false, swipeSensitivity: 100 });

  // V6.0: 不喜欢列表
  const [dislikes, setDislikes] = useState<string[]>([]);
  const [selectedDislikes, setSelectedDislikes] = useState<string[]>([]);

  // V6.0: 从服务器加载全局播放设置
  useEffect(() => {
    const fetchPlaybackSettings = async () => {
      try {
        const response = await fetch('/api/settings/playback');
        if (response.ok) {
          const data = await response.json();
          if (data.success && data.settings) {
            setPlaybackSettings(data.settings);
          }
        }
      } catch (e) {
        console.error('获取全局播放设置失败:', e);
      }
    };
    fetchPlaybackSettings();
  }, []);

  // V6.0: 加载不喜欢列表
  useEffect(() => {
    const fetchDislikes = async () => {
      try {
        const response = await fetch('/api/dislikes');
        if (response.ok) {
          const data = await response.json();
          if (data.success && data.dislikes) {
            setDislikes(data.dislikes);
          }
        }
      } catch (e) {
        console.error('获取不喜欢列表失败:', e);
      }
    };
    fetchDislikes();
  }, []);
  // 使用 useMemo 缓存统计计算
  const stats = useMemo(() => {
    if (videos.length === 0) {
      return {
        totalVideos: 0,
        totalSize: 0,
        favoriteCount: 0,
        avgCompletionRate: 0,
        totalViews: 0,
        totalWatchTime: 0,
        avgViewDuration: 0,
        topGroups: [],
        topTags: [],
        recentUploads: 0,
        engagementRate: 0
      };
    }
    return calculateStatsPure(videos);
  }, [videos]);

  // 全屏切换功能
  const toggleFullscreen = useCallback(() => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().then(() => {
        setIsFullscreen(true);
      }).catch(err => {
        console.error('全屏模式错误:', err);
      });
    } else {
      document.exitFullscreen().then(() => {
        setIsFullscreen(false);
      }).catch(err => {
        console.error('退出全屏错误:', err);
      });
    }
  }, []);

  // 监听全屏变化事件
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, []);

  // 获取所有分组
  const getAllGroups = useCallback(() => {
    const groups = new Set<string>();
    videos.forEach(video => groups.add(video.group));
    return Array.from(groups).sort();
  }, [videos]);

  // 使用 useMemo 缓存筛选和排序结果，避免每次渲染重复计算
  const filteredVideos = useMemo(() => {
    let filtered = [...videos];
    
    // 搜索过滤
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(video => 
        (video.title?.toLowerCase().includes(term) || '') ||
        (video.description?.toLowerCase().includes(term) || '') ||
        video.tags.some(tag => tag.toLowerCase().includes(term)) ||
        video.filename.toLowerCase().includes(term)
      );
    }
    
    // 分组过滤
    if (filterGroup !== 'all') {
      filtered = filtered.filter(video => video.group === filterGroup);
    }
    
    // 排序
    return filtered.sort((a, b) => {
      switch (sortBy) {
        case 'uploadDate':
          return new Date(b.uploadDate).getTime() - new Date(a.uploadDate).getTime();
        case 'viewCount':
          return (b.viewCount || 0) - (a.viewCount || 0);
        case 'completionRate':
          return (b.watchTimeStats?.completionRate || 0) - (a.watchTimeStats?.completionRate || 0);
        case 'title':
          return (a.title || a.filename).localeCompare(b.title || b.filename);
        case 'size':
          return b.size - a.size;
        default:
          return 0;
      }
    });
  }, [videos, searchTerm, filterGroup, sortBy]);

  // 分页数据
  const totalPages = Math.ceil(filteredVideos.length / PAGE_SIZE);
  const paginatedVideos = useMemo(() => {
    return filteredVideos.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);
  }, [filteredVideos, currentPage]);

  // 搜索/筛选/排序变化时重置到第一页
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filterGroup, sortBy]);

  // 获取视频列表
  // 下载抖音视频
  const downloadDouyinVideo = async (url: string) => {
    try {
      setIsUploading(true);
      setError(null);
      
      const response = await fetch('/api/videos/download', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ url }),
      });
      
      if (response.ok) {
        const result = await response.json();
        console.log('视频下载任务已创建:', result.id);
        
        // 轮询检查下载状态
        const checkStatus = async () => {
          const statusResponse = await fetch(`/api/videos/download/${result.id}/status`);
          if (statusResponse.ok) {
            const statusData = await statusResponse.json();
            if (statusData.status === 'completed') {
              // 下载完成，刷新视频列表
              fetchVideos();
              setIsUploading(false);
              alert('视频下载完成！');
            } else {
              // 继续轮询
              setTimeout(checkStatus, 2000);
            }
          } else {
            setError('检查下载状态失败');
            setIsUploading(false);
          }
        };
        
        // 开始轮询
        setTimeout(checkStatus, 2000);
        
      } else {
        const errorData = await response.json();
        setError(errorData.message || '视频下载失败');
        setIsUploading(false);
      }
    } catch (err) {
      setError('网络错误');
      setIsUploading(false);
    }
  };

  const fetchVideos = async () => {
    try {
      setError(null); // 清除之前的错误
      const response = await fetch('/api/videos');
      if (response.ok) {
        const data = await response.json();
        setVideos(data);
      } else {
        setError(`获取视频列表失败: ${response.status} ${response.statusText}`);
      }
    } catch (err) {
      setError('获取视频列表失败: 网络错误');
      console.error('获取视频列表错误:', err);
    }
  };

  useEffect(() => {
    fetchVideos();
  }, []);

  // 处理文件上传
  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    setIsUploading(true);
    setUploadProgress(0);
    setError(null);

    const formData = new FormData();
    // 支持批量上传，显示上传数量
    console.log(`准备上传 ${files.length} 个视频文件`);
    for (let i = 0; i < files.length; i++) {
      formData.append('videos', files[i]);
    }

    try {
      const response = await fetch('/api/videos/upload', {
        method: 'POST',
        body: formData,
      });

      if (response.ok || response.status === 207) {
        const result = await response.json();
        await fetchVideos();
        setUploadProgress(100);
        
        let message = '';
        if (result.count > 0) {
          message += `成功上传 ${result.count} 个视频文件！`;
        }
        if (result.errors && result.errors.length > 0) {
          message += `\n\n失败的文件：\n${result.errors.join('\n')}`;
          // 显示错误信息
          setError(result.errors.join('\n'));
        }
        
        if (message) {
          alert(message);
        }
      } else {
        const errorData = await response.json();
        setError(errorData.message || '上传失败');
        alert('上传失败：' + (errorData.message || '未知错误'));
      }
    } catch (err) {
      setError('上传过程中发生错误');
    } finally {
      setIsUploading(false);
      // 重置文件输入
      if (event.target) {
        event.target.value = '';
      }
    }
  };

  // 更新视频标签
  const handleUpdateTags = async (videoId: string, tagsString: string) => {
    const tags = tagsString
      .split(',')
      .map(tag => tag.trim())
      .filter(tag => tag.length > 0);
    
    try {
      const response = await fetch(`/api/videos/${videoId}/metadata`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          tags,
          description: videos.find(v => v.id === videoId)?.description || '',
          group: videos.find(v => v.id === videoId)?.group || '默认分组'
        }),
      });

      if (response.ok) {
        const updatedMetadata = await response.json();
        setVideos(videos.map(video => 
          video.id === videoId 
            ? { ...video, tags: updatedMetadata.metadata.tags }
            : video
        ));
      }
    } catch (err) {
      console.error('更新标签失败:', err);
    }
  };

  // 更新视频分组
  const handleUpdateGroup = async (videoId: string, group: string) => {
    try {
      const response = await fetch(`/api/videos/${videoId}/metadata`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          tags: videos.find(v => v.id === videoId)?.tags || [],
          description: videos.find(v => v.id === videoId)?.description || '',
          group: group || '默认分组'
        }),
      });

      if (response.ok) {
        const updatedMetadata = await response.json();
        setVideos(videos.map(video => 
          video.id === videoId 
            ? { ...video, group: updatedMetadata.metadata.group }
            : video
        ));
      }
    } catch (err) {
      console.error('更新分组失败:', err);
    }
  };

  // 更新视频标题
  const handleUpdateTitle = async (videoId: string, title: string) => {
    try {
      const response = await fetch(`/api/videos/${videoId}/metadata`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          tags: videos.find(v => v.id === videoId)?.tags || [],
          description: videos.find(v => v.id === videoId)?.description || '',
          group: videos.find(v => v.id === videoId)?.group || '默认分组',
          title: title || ''
        }),
      });

      if (response.ok) {
        const updatedMetadata = await response.json();
        setVideos(videos.map(video => 
          video.id === videoId 
            ? { ...video, title: updatedMetadata.metadata.title }
            : video
        ));
      }
    } catch (err) {
      console.error('更新标题失败:', err);
    }
  };

  // 更新视频描述
  const handleUpdateDescription = async (videoId: string, description: string) => {
    try {
      const response = await fetch(`/api/videos/${videoId}/metadata`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          tags: videos.find(v => v.id === videoId)?.tags || [],
          description,
          group: videos.find(v => v.id === videoId)?.group || '默认分组'
        }),
      });

      if (response.ok) {
        const updatedMetadata = await response.json();
        setVideos(videos.map(video => 
          video.id === videoId 
            ? { ...video, description: updatedMetadata.metadata.description }
            : video
        ));
      }
    } catch (err) {
      console.error('更新描述失败:', err);
    }
  };

  // 保存编辑（统一提交所有更改）
  const handleSaveEdit = async () => {
    if (!editingVideo) {
      console.error('保存失败: editingVideo 为 null');
      return;
    }
    
    console.log('开始保存编辑，视频ID:', editingVideo.id);
    console.log('表单数据:', editFormData);
    
    const tags = editFormData.tags
      .split(',')
      .map(tag => tag.trim())
      .filter(tag => tag.length > 0);
    
    console.log('处理后的标签:', tags);
    
    try {
      const response = await fetch(`/api/videos/${editingVideo.id}/metadata`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          tags,
          description: editFormData.description,
          group: editFormData.group || '默认分组',
          title: editFormData.title
        }),
      });

      console.log('保存响应状态:', response.status);

      if (response.ok) {
        const result = await response.json();
        console.log('保存成功，返回数据:', result);
        
        // 使用服务器返回的 metadata 更新本地状态
        const newMetadata = result.metadata;
        setVideos(prevVideos => prevVideos.map(video => 
          video.id === editingVideo.id 
            ? { 
                ...video, 
                title: newMetadata.title,
                description: newMetadata.description,
                tags: newMetadata.tags,
                group: newMetadata.group
              }
            : video
        ));
        setEditingVideo(null);
        console.log('本地状态已更新');
      } else {
        const errorData = await response.json();
        console.error('保存失败:', errorData);
        setError('保存失败: ' + (errorData.message || '未知错误'));
      }
    } catch (err) {
      console.error('保存编辑失败:', err);
      setError('保存过程中发生错误');
    }
  };

  // 删除视频（软删除到回收站）
  const handleDeleteVideo = async (videoId: string) => {
    if (!window.confirm('确定要删除这个视频吗？（可在回收站恢复）')) return;

    try {
      const response = await fetch(`/api/videos/${videoId}/trash`, {
        method: 'POST',
      });

      if (response.ok) {
        setVideos(videos.filter(video => video.id !== videoId));
        setSelectedVideos(selectedVideos.filter(id => id !== videoId));
      } else {
        setError('删除视频失败');
      }
    } catch (err) {
      setError('删除过程中发生错误');
    }
  };

  // 批量删除视频（软删除到回收站）
  const handleBatchDelete = async () => {
    if (selectedVideos.length === 0) return;
    
    if (!window.confirm(`确定要删除选中的 ${selectedVideos.length} 个视频吗？（可在回收站恢复）`)) return;
    
    try {
      const deletePromises = selectedVideos.map(id => 
        fetch(`/api/videos/${id}/trash`, { method: 'POST' })
      );
      
      const results = await Promise.all(deletePromises);
      const successfulDeletes = results.filter(result => result.ok).length;
      
      if (successfulDeletes > 0) {
        setVideos(videos.filter(video => !selectedVideos.includes(video.id)));
        setSelectedVideos([]);
        alert(`成功删除 ${successfulDeletes} 个视频（已移到回收站）`);
      } else {
        setError('批量删除失败');
      }
    } catch (err) {
      setError('批量删除过程中发生错误');
    }
  };

  // 批量收藏/取消收藏
  const handleBatchFavorite = async (favorite: boolean) => {
    if (selectedVideos.length === 0) return;
    
    try {
      const favoritePromises = selectedVideos.map(id => 
        fetch(`/api/videos/${id}/favorite`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ favorite })
        })
      );
      
      await Promise.all(favoritePromises);
      
      // 更新本地状态
      setVideos(videos.map(video => 
        selectedVideos.includes(video.id) 
          ? { ...video, isFavorite: favorite }
          : video
      ));
      
      alert(favorite ? `已收藏 ${selectedVideos.length} 个视频` : `已取消收藏 ${selectedVideos.length} 个视频`);
    } catch (err) {
      setError('批量收藏操作失败');
    }
  };

  // 批量添加标签
  const handleBatchAddTags = async (tagsString: string) => {
    if (selectedVideos.length === 0 || !tagsString.trim()) return;
    
    const newTags = tagsString
      .split(',')
      .map(tag => tag.trim())
      .filter(tag => tag.length > 0);
    
    if (newTags.length === 0) return;
    
    try {
      const updatePromises = selectedVideos.map(videoId => {
        const existingVideo = videos.find(v => v.id === videoId);
        if (!existingVideo) return Promise.resolve();
        
        const mergedTags = [...new Set([...existingVideo.tags, ...newTags])];
        
        return fetch(`/api/videos/${videoId}/metadata`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            tags: mergedTags,
            description: existingVideo.description || '',
            group: existingVideo.group || '默认分组'
          })
        });
      });
      
      await Promise.all(updatePromises);
      
      // 刷新视频列表
      await fetchVideos();
      
      alert(`已为 ${selectedVideos.length} 个视频添加标签: ${newTags.join(', ')}`);
    } catch (err) {
      setError('批量添加标签失败');
    }
  };

  // 批量编辑标题
  const handleBatchEditTitle = async (titlePrefix: string) => {
    if (selectedVideos.length === 0 || !titlePrefix.trim()) return;
    
    try {
      const updatePromises = selectedVideos.map(videoId => {
        const existingVideo = videos.find(v => v.id === videoId);
        if (!existingVideo) return Promise.resolve();
        
        const newTitle = titlePrefix + (existingVideo.title || existingVideo.filename);
        
        return fetch(`/api/videos/${videoId}/metadata`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            tags: existingVideo.tags || [],
            description: existingVideo.description || '',
            group: existingVideo.group || '默认分组',
            title: newTitle
          })
        });
      });
      
      await Promise.all(updatePromises);
      
      // 刷新视频列表
      await fetchVideos();
      
      alert(`已为 ${selectedVideos.length} 个视频批量编辑标题`);
    } catch (err) {
      setError('批量编辑标题失败');
    }
  };

  // 批量编辑描述
  const handleBatchEditDescription = async (description: string) => {
    if (selectedVideos.length === 0 || !description.trim()) return;
    
    try {
      const updatePromises = selectedVideos.map(videoId => {
        const existingVideo = videos.find(v => v.id === videoId);
        if (!existingVideo) return Promise.resolve();
        
        return fetch(`/api/videos/${videoId}/metadata`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            tags: existingVideo.tags || [],
            description: description,
            group: existingVideo.group || '默认分组'
          })
        });
      });
      
      await Promise.all(updatePromises);
      
      // 刷新视频列表
      await fetchVideos();
      
      alert(`已为 ${selectedVideos.length} 个视频批量编辑描述`);
    } catch (err) {
      setError('批量编辑描述失败');
    }
  };

  // 批量移动到分组
  const handleBatchMoveToGroup = async (groupName: string) => {
    if (selectedVideos.length === 0 || !groupName.trim()) return;
    
    try {
      const updatePromises = selectedVideos.map(videoId => {
        const existingVideo = videos.find(v => v.id === videoId);
        if (!existingVideo) return Promise.resolve();
        
        return fetch(`/api/videos/${videoId}/metadata`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            tags: existingVideo.tags || [],
            description: existingVideo.description || '',
            group: groupName
          })
        });
      });
      
      await Promise.all(updatePromises);
      
      // 刷新视频列表
      await fetchVideos();
      
      alert(`已将 ${selectedVideos.length} 个视频移动到分组: ${groupName}`);
    } catch (err) {
      setError('批量移动分组失败');
    }
  };

  // 一键自动分组
  const handleAutoGroup = async () => {
    try {
      setIsAutoGrouping(true);
      
      // 如果选中了视频，只处理选中的；否则处理全部
      const targetIds = selectedVideos.length > 0 ? selectedVideos : [];
      
      const response = await fetch('/api/videos/auto-group', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          strategy: autoGroupStrategy,
          targetIds
        })
      });
      
      if (response.ok) {
        const result = await response.json();
        await fetchVideos();
        
        // 构建分组变化摘要
        const groupChanges: Record<string, number> = {};
        result.results.forEach((item: any) => {
          if (item.oldGroup !== item.newGroup) {
            groupChanges[item.newGroup] = (groupChanges[item.newGroup] || 0) + 1;
          }
        });
        
        const changedCount = result.results.filter((item: any) => item.oldGroup !== item.newGroup).length;
        
        let message = `✅ ${result.message}`;
        if (changedCount > 0) {
          message += `\n\n分组变化:`;
          Object.entries(groupChanges).forEach(([group, count]) => {
            message += `\n  • ${group}: ${count}个`;
          });
        } else {
          message += `\n（没有视频需要调整分组）`;
        }
        
        alert(message);
      } else {
        const errorData = await response.json();
        alert('自动分组失败: ' + (errorData.message || '未知错误'));
      }
    } catch (err) {
      console.error('自动分组失败:', err);
      alert('自动分组失败，请检查网络连接');
    } finally {
      setIsAutoGrouping(false);
      setShowAutoGroupModal(false);
    }
  };

  // 批量导入处理函数
  const handleBatchImport = async () => {
    if (!importDir) {
      alert('请输入导入目录路径');
      return;
    }
    
    try {
      setIsImporting(true);
      setImportResult(null);
      
      const response = await fetch('/api/batch-import', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          importDir,
          metadataMode: importMetadataMode,
          defaultGroup: importDefaultGroup,
          autoTag: importAutoTag,
          skipDuplicates: importSkipDuplicates,
          validateVideos: importValidateVideos // 传递校验参数
        })
      });
      
      if (response.ok) {
        const result = await response.json();
        setImportResult(result);
        
        // 刷新视频列表
        await fetchVideos();
        
        // 显示结果摘要
        let message = `✅ 批量导入完成！\n\n`;
        message += `总计: ${result.summary.total}\n`;
        message += `成功: ${result.summary.imported}\n`;
        message += `跳过: ${result.summary.skipped}\n`;
        message += `失败: ${result.summary.failed}`;
        
        if (result.details.failed.length > 0) {
          message += `\n\n失败详情:`;
          result.details.failed.forEach((item: any) => {
            message += `\n  • ${item.filename}: ${item.error}`;
          });
        }
        
        alert(message);
      } else {
        const errorData = await response.json();
        alert('批量导入失败: ' + (errorData.message || '未知错误'));
      }
    } catch (err) {
      console.error('批量导入失败:', err);
      alert('批量导入失败，请检查网络连接');
    } finally {
      setIsImporting(false);
    }
  };

  // V6.0: 保存全局播放设置到服务器
  const savePlaybackSettings = async (settings: { shuffle: boolean; group: string | string[]; recommend: boolean; coldVideoInsert?: boolean; coldVideoInterval?: number; maxColdVideos?: number; loopPlayback?: boolean; swipeSensitivity?: number }) => {
    setPlaybackSettings(settings as any);
    try {
      const response = await fetch('/api/settings/playback', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(settings)
      });
      if (!response.ok) {
        console.error('保存全局播放设置失败');
      }
    } catch (e) {
      console.error('保存全局播放设置异常:', e);
    }
  };

  // 导出视频数据
  const handleExportVideos = async () => {
    try {
      const exportData = videos.map(video => ({
        id: video.id,
        filename: video.filename,
        title: video.title || '',
        description: video.description || '',
        tags: video.tags || [],
        group: video.group || '默认分组',
        uploadDate: video.uploadDate,
        size: video.size,
        viewCount: video.viewCount || 0,
        likeCount: video.likeCount || 0,
        isFavorite: video.isFavorite || false,
        watchTimeStats: video.watchTimeStats || null
      }));
      
      const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `vdio-videos-export-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      alert(`成功导出 ${videos.length} 个视频的数据！`);
    } catch (err) {
      setError('导出数据失败');
    }
  };

  // 导入视频数据
  const handleImportVideos = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    try {
      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          const importedData = JSON.parse(e.target?.result as string);
          
          if (!Array.isArray(importedData)) {
            throw new Error('导入的数据格式不正确');
          }
          
          let successCount = 0;
          let errorCount = 0;
          
          // 验证并更新每个视频的元数据
          for (const item of importedData) {
            try {
              // 检查视频文件是否存在
              const videoExists = videos.some(v => v.id === item.id);
              if (!videoExists) {
                errorCount++;
                continue;
              }
              
              // 更新元数据
              const response = await fetch(`/api/videos/${item.id}/metadata`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  tags: item.tags || [],
                  description: item.description || '',
                  group: item.group || '默认分组',
                  title: item.title || ''
                })
              });
              
              if (response.ok) {
                successCount++;
              } else {
                errorCount++;
              }
            } catch (updateError) {
              console.error('更新视频元数据失败:', updateError);
              errorCount++;
            }
          }
          
          // 刷新视频列表
          await fetchVideos();
          
          let message = `成功导入 ${successCount} 个视频的元数据`;
          if (errorCount > 0) {
            message += `，${errorCount} 个失败（可能视频文件不存在）`;
          }
          
          alert(message);
          
        } catch (parseError) {
          setError('导入文件格式错误，请选择正确的JSON文件');
        }
      };
      
      reader.onerror = () => {
        setError('读取文件失败');
      };
      
      reader.readAsText(file);
      
      // 重置文件输入
      if (event.target) {
        event.target.value = '';
      }
      
    } catch (err) {
      setError('导入数据失败');
    }
  };

  return (
    <div className="h-screen bg-gray-900 text-white p-3 md:p-6 overflow-y-auto mobile-scroll-container prevent-zoom">
      <div className="max-w-7xl mx-auto">
        {/* 顶部标题和操作栏 */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-6 gap-4">
          <div>
            <h1 className="text-3xl font-bold mb-2">🎥 视频管理后台</h1>
            <div className="flex flex-wrap gap-4 text-sm text-gray-400">
              <span>视频: {stats.totalVideos}</span>
              <span>大小: {(stats.totalSize / 1024 / 1024).toFixed(1)} MB</span>
              <span>播放: {stats.totalViews.toLocaleString()}</span>
              <span>完播: {stats.avgCompletionRate}%</span>
            </div>
          </div>
          
          {/* 主要操作按钮 */}
          <div className="flex flex-wrap gap-2 md:gap-3">
            {/* 全屏切换按钮 */}
            <button
              onClick={toggleFullscreen}
              className="bg-gradient-to-r from-gray-600 to-gray-700 hover:from-gray-700 hover:to-gray-800 px-3 py-1.5 md:px-4 md:py-2 rounded font-medium transition-all duration-200 flex items-center gap-1 md:gap-2 text-xs md:text-base shadow-md md:shadow-lg"
              title={isFullscreen ? '退出全屏' : '进入全屏'}
            >
              {isFullscreen ? '⛶ 退出' : '⛶ 全屏'}
            </button>
            <button
              onClick={() => setShowUploadModal(true)}
              className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 px-3 py-1.5 md:px-4 md:py-2 rounded font-medium transition-all duration-200 flex items-center gap-1 md:gap-2 text-xs md:text-base shadow-md md:shadow-lg"
            >
              📤 上传
            </button>
            <button
              onClick={() => setShowBatchImportModal(true)}
              className="bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 px-3 py-1.5 md:px-4 md:py-2 rounded font-medium transition-all duration-200 flex items-center gap-1 md:gap-2 text-xs md:text-base shadow-md md:shadow-lg"
              title="从目录批量导入视频"
            >
              📦 批量导入
            </button>
            <a 
              href="/admin/collection" 
              className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 px-3 py-1.5 md:px-4 md:py-2 rounded font-medium transition-all duration-200 flex items-center gap-1 md:gap-2 text-xs md:text-base shadow-md md:shadow-lg"
            >
              🔗 采集
            </a>
            <a 
              href="/admin/analytics" 
              className="bg-gradient-to-r from-orange-600 to-red-600 hover:from-orange-700 hover:to-red-700 px-3 py-1.5 md:px-4 md:py-2 rounded font-medium transition-all duration-200 flex items-center gap-1 md:gap-2 text-xs md:text-base shadow-md md:shadow-lg"
            >
              📊 分析
            </a>
            <button
              onClick={() => setShowAutoGroupModal(true)}
              className="bg-gradient-to-r from-cyan-600 to-teal-600 hover:from-cyan-700 hover:to-teal-700 px-3 py-1.5 md:px-4 md:py-2 rounded font-medium transition-all duration-200 flex items-center gap-1 md:gap-2 text-xs md:text-base shadow-md md:shadow-lg"
              title="一键自动分组"
            >
              🤖 分组
            </button>
            <button
              onClick={handleExportVideos}
              className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 px-3 py-1.5 md:px-4 md:py-2 rounded font-medium transition-all duration-200 flex items-center gap-1 md:gap-2 text-xs md:text-base shadow-md md:shadow-lg"
            >
              📥 导出
            </button>
            <label 
              htmlFor="import-file" 
              className="bg-gradient-to-r from-indigo-600 to-blue-600 hover:from-indigo-700 hover:to-blue-700 px-3 py-1.5 md:px-4 md:py-2 rounded font-medium transition-all duration-200 flex items-center gap-1 md:gap-2 text-xs md:text-base shadow-md md:shadow-lg cursor-pointer"
            >
              📤 导入
              <input
                type="file"
                id="import-file"
                accept=".json"
                onChange={handleImportVideos}
                className="hidden"
              />
            </label>
          </div>
        </div>
        
        {/* 统计仪表板 */}
        <div className="bg-gray-800/40 backdrop-blur-sm rounded-lg p-4 mb-4 border border-gray-700/50">
          <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
            📈 数据概览
            <button 
              onClick={() => fetchVideos()}
              className="text-xs bg-blue-600 hover:bg-blue-700 px-2 py-1 rounded transition-colors"
              title="刷新统计数据"
            >
              🔄
            </button>
          </h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
            <div className="bg-gray-700/50 p-3 rounded-lg text-center">
              <div className="text-2xl font-bold text-green-400">{stats.totalVideos}</div>
              <div className="text-xs text-gray-400">总视频</div>
            </div>
            <div className="bg-gray-700/50 p-3 rounded-lg text-center">
              <div className="text-2xl font-bold text-blue-400">{stats.totalViews.toLocaleString()}</div>
              <div className="text-xs text-gray-400">总播放</div>
            </div>
            <div className="bg-gray-700/50 p-3 rounded-lg text-center">
              <div className="text-2xl font-bold text-purple-400">{stats.avgCompletionRate}%</div>
              <div className="text-xs text-gray-400">完播率</div>
            </div>
            <div className="bg-gray-700/50 p-3 rounded-lg text-center">
              <div className="text-2xl font-bold text-yellow-400">{stats.engagementRate}%</div>
              <div className="text-xs text-gray-400">参与度</div>
            </div>
            <div className="bg-gray-700/50 p-3 rounded-lg text-center">
              <div className="text-2xl font-bold text-orange-400">{stats.recentUploads}</div>
              <div className="text-xs text-gray-400">近7天</div>
            </div>
            <div className="bg-gray-700/50 p-3 rounded-lg text-center">
              <div className="text-2xl font-bold text-red-400">{stats.favoriteCount}</div>
              <div className="text-xs text-gray-400">收藏数</div>
            </div>
          </div>
          
          {/* 详细统计 */}
          <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* 热门分组 */}
            <div>
              <h4 className="text-sm font-medium text-gray-300 mb-2">🔥 热门分组</h4>
              <div className="space-y-2">
                {stats.topGroups.map((group, index) => (
                  <div key={group.name} className="flex justify-between items-center text-sm">
                    <span className="text-white">{group.name}</span>
                    <span className="text-gray-400">{group.count}个</span>
                  </div>
                ))}
                {stats.topGroups.length === 0 && (
                  <div className="text-gray-500 text-sm">暂无分组数据</div>
                )}
              </div>
            </div>
            
            {/* 热门标签 */}
            <div>
              <h4 className="text-sm font-medium text-gray-300 mb-2">🏷️ 热门标签</h4>
              <div className="space-y-2">
                {stats.topTags.map((tag, index) => (
                  <div key={tag.name} className="flex justify-between items-center text-sm">
                    <span className="text-white">{tag.name}</span>
                    <span className="text-gray-400">{tag.count}次</span>
                  </div>
                ))}
                {stats.topTags.length === 0 && (
                  <div className="text-gray-500 text-sm">暂无标签数据</div>
                )}
              </div>
            </div>
          </div>
        </div>
        
        {/* 播放设置 */}
        <div className="bg-gray-800/40 backdrop-blur-sm rounded-lg p-4 mb-4 border border-gray-700/50">
          <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
            🎬 播放设置
            <span className="text-xs text-gray-500 font-normal">（设置后刷新首页生效）</span>
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {/* 随机播放开关 */}
            <div className="flex items-center justify-between bg-gray-700/50 p-3 rounded-lg">
              <div className="flex items-center gap-3">
                <div className="text-xl">🔀</div>
                <div>
                  <div className="text-sm font-medium text-white">随机播放</div>
                  <div className="text-xs text-gray-400">打乱视频播放顺序</div>
                </div>
              </div>
              <button
                onClick={() => {
                  const newSettings = { ...playbackSettings, shuffle: !playbackSettings.shuffle };
                  savePlaybackSettings(newSettings);
                }}
                className={`relative w-12 h-6 rounded-full transition-colors duration-200 ${
                  playbackSettings.shuffle ? 'bg-green-500' : 'bg-gray-600'
                }`}
              >
                <div
                  className={`absolute top-0.5 w-5 h-5 bg-white rounded-full transition-transform duration-200 ${
                    playbackSettings.shuffle ? 'translate-x-6' : 'translate-x-0.5'
                  }`}
                />
              </button>
            </div>
            
            {/* 智能推荐开关 */}
            <div className="flex items-center justify-between bg-gray-700/50 p-3 rounded-lg">
              <div className="flex items-center gap-3">
                <div className="text-xl">🤖</div>
                <div>
                  <div className="text-sm font-medium text-white">智能推荐</div>
                  <div className="text-xs text-gray-400">根据用户兴趣推荐视频</div>
                </div>
              </div>
              <button
                onClick={() => {
                  const newSettings = { ...playbackSettings, recommend: !playbackSettings.recommend };
                  savePlaybackSettings(newSettings);
                }}
                className={`relative w-12 h-6 rounded-full transition-colors duration-200 ${
                  playbackSettings.recommend ? 'bg-green-500' : 'bg-gray-600'
                }`}
              >
                <div
                  className={`absolute top-0.5 w-5 h-5 bg-white rounded-full transition-transform duration-200 ${
                    playbackSettings.recommend ? 'translate-x-6' : 'translate-x-0.5'
                  }`}
                />
              </button>
            </div>
            
            {/* 冷门视频插播开关 */}
            <div className="flex items-center justify-between bg-gray-700/50 p-3 rounded-lg">
              <div className="flex items-center gap-3">
                <div className="text-xl">🔍</div>
                <div>
                  <div className="text-sm font-medium text-white">冷门视频探索</div>
                  <div className="text-xs text-gray-400">随机插播被忽视的优质视频</div>
                </div>
              </div>
              <button
                onClick={() => {
                  const newSettings = { ...playbackSettings, coldVideoInsert: !playbackSettings.coldVideoInsert };
                  savePlaybackSettings(newSettings);
                }}
                className={`relative w-12 h-6 rounded-full transition-colors duration-200 ${
                  playbackSettings.coldVideoInsert ? 'bg-purple-500' : 'bg-gray-600'
                }`}
              >
                <div
                  className={`absolute top-0.5 w-5 h-5 bg-white rounded-full transition-transform duration-200 ${
                    playbackSettings.coldVideoInsert ? 'translate-x-6' : 'translate-x-0.5'
                  }`}
                />
              </button>
            </div>
            
            {/* 冷门视频插播配置（仅在开启时显示） */}
            {playbackSettings.coldVideoInsert && (
              <div className="bg-gray-700/30 p-3 rounded-lg space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-400">插播间隔</span>
                  <select
                    value={playbackSettings.coldVideoInterval || 5}
                    onChange={(e) => {
                      const newSettings = { ...playbackSettings, coldVideoInterval: parseInt(e.target.value) };
                      savePlaybackSettings(newSettings);
                    }}
                    className="px-2 py-1 bg-gray-600 border border-gray-500 rounded text-white text-xs focus:outline-none"
                  >
                    <option value="3">每3个视频</option>
                    <option value="5">每5个视频</option>
                    <option value="8">每8个视频</option>
                    <option value="10">每10个视频</option>
                  </select>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-400">最多插播数量</span>
                  <select
                    value={playbackSettings.maxColdVideos || 3}
                    onChange={(e) => {
                      const newSettings = { ...playbackSettings, maxColdVideos: parseInt(e.target.value) };
                      savePlaybackSettings(newSettings);
                    }}
                    className="px-2 py-1 bg-gray-600 border border-gray-500 rounded text-white text-xs focus:outline-none"
                  >
                    <option value="1">1个</option>
                    <option value="2">2个</option>
                    <option value="3">3个</option>
                    <option value="5">5个</option>
                  </select>
                </div>
              </div>
            )}
            
            {/* 循环播放开关 */}
            <div className="flex items-center justify-between bg-gray-700/50 p-3 rounded-lg">
              <div className="flex items-center gap-3">
                <div className="text-xl">🔁</div>
                <div>
                  <div className="text-sm font-medium text-white">循环播放</div>
                  <div className="text-xs text-gray-400">视频结束后重复播放当前视频</div>
                </div>
              </div>
              <button
                onClick={() => {
                  const newSettings = { ...playbackSettings, loopPlayback: !playbackSettings.loopPlayback };
                  savePlaybackSettings(newSettings);
                }}
                className={`relative w-12 h-6 rounded-full transition-colors duration-200 ${
                  playbackSettings.loopPlayback ? 'bg-green-500' : 'bg-gray-600'
                }`}
              >
                <div
                  className={`absolute top-0.5 w-5 h-5 bg-white rounded-full transition-transform duration-200 ${
                    playbackSettings.loopPlayback ? 'translate-x-6' : 'translate-x-0.5'
                  }`}
                />
              </button>
            </div>
            
            {/* 滑动灵敏度配置 */}
            <div className="bg-gray-700/50 p-3 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-3">
                  <div className="text-xl">👆</div>
                  <div>
                    <div className="text-sm font-medium text-white">滑动灵敏度</div>
                    <div className="text-xs text-gray-400">控制切换视频的难易程度</div>
                  </div>
                </div>
                <div className="text-sm font-bold text-blue-400">{typeof playbackSettings.swipeSensitivity === 'number' ? playbackSettings.swipeSensitivity : 50}</div>
              </div>
              <input
                type="range"
                min="1"
                max="100"
                value={typeof playbackSettings.swipeSensitivity === 'number' ? playbackSettings.swipeSensitivity : 50}
                onChange={(e) => {
                  const newSettings = { ...playbackSettings, swipeSensitivity: parseInt(e.target.value) };
                  savePlaybackSettings(newSettings);
                }}
                className="w-full h-2 bg-gray-600 rounded-lg appearance-none cursor-pointer accent-blue-500"
              />
              <div className="flex justify-between text-xs text-gray-500 mt-1">
                <span>迟钝（需大幅滑动）</span>
                <span>灵敏（轻微滑动）</span>
              </div>
            </div>
            
            {/* V6.1: 指定分组播放（支持多选） */}
            <div className="bg-gray-700/50 p-3 rounded-lg">
              <div className="flex items-center gap-3 mb-3">
                <div className="text-xl"></div>
                <div>
                  <div className="text-sm font-medium text-white">指定分组播放</div>
                  <div className="text-xs text-gray-400">勾选要播放的分组（可多选）</div>
                </div>
              </div>
              
              {/* V6.1: 复选框列表 */}
              <div className="space-y-2 max-h-[200px] overflow-y-auto pr-2">
                {/* 全部视频选项 */}
                <label className="flex items-center gap-3 cursor-pointer hover:bg-gray-600/50 p-2 rounded transition-colors group">
                  <input
                    type="checkbox"
                    checked={playbackSettings.group === 'all' || (Array.isArray(playbackSettings.group) && playbackSettings.group.length === 0)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        savePlaybackSettings({ ...playbackSettings, group: 'all' });
                      } else {
                        const allGroups = getAllGroups();
                        if (allGroups.length > 0) {
                          savePlaybackSettings({ ...playbackSettings, group: [allGroups[0]] });
                        }
                      }
                    }}
                    className="w-4 h-4 rounded border-gray-500 accent-blue-600 flex-shrink-0 cursor-pointer"
                  />
                  <span className="text-sm text-white font-medium">全部视频</span>
                  <span className="text-xs text-gray-400 ml-auto">播放所有分组</span>
                </label>
                
                <div className="border-t border-gray-600 my-2"></div>
                
                {getAllGroups().map(group => {
                  const isSelected = Array.isArray(playbackSettings.group) 
                    ? playbackSettings.group.includes(group)
                    : playbackSettings.group === group;
                  
                  return (
                    <label key={group} className="flex items-center gap-3 cursor-pointer hover:bg-gray-600/50 p-2 rounded transition-colors group">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={(e) => {
                          let currentGroups = Array.isArray(playbackSettings.group) 
                            ? [...playbackSettings.group]
                            : playbackSettings.group === 'all' ? [] : [playbackSettings.group];
                          
                          if (e.target.checked) {
                            if (!currentGroups.includes(group)) {
                              currentGroups.push(group);
                            }
                          } else {
                            currentGroups = currentGroups.filter(g => g !== group);
                          }
                          
                          const newGroup = currentGroups.length === 0 ? 'all' : currentGroups.join(',');
                          savePlaybackSettings({ ...playbackSettings, group: newGroup });
                        }}
                        className="w-4 h-4 rounded border-gray-500 accent-blue-600 flex-shrink-0 cursor-pointer"
                      />
                      <span className="text-sm text-white">{group}</span>
                      {isSelected && (
                        <span className="text-xs text-green-400 ml-auto">✓ 已选中</span>
                      )}
                    </label>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
        
        {/* V6.0: 不喜欢列表管理 */}
        <div className="bg-gray-800/40 backdrop-blur-sm rounded-lg p-4 mb-4 border border-gray-700/50">
          <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
            👎 不喜欢列表
            <span className="text-xs text-gray-500 font-normal">（共 {dislikes.length} 个作品）</span>
          </h3>
          
          {dislikes.length === 0 ? (
            <div className="text-gray-400 text-sm py-4 text-center">
              暂无标记为不喜欢的作品
            </div>
          ) : (
            <>
              <div className="max-h-[300px] overflow-y-auto mb-3">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                  {dislikes.map(videoId => {
                    const video = videos.find(v => v.id === videoId);
                    return (
                      <div
                        key={videoId}
                        className="flex items-center gap-2 bg-gray-700/50 p-2 rounded-lg hover:bg-gray-700 transition-colors"
                      >
                        <input
                          type="checkbox"
                          checked={selectedDislikes.includes(videoId)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedDislikes([...selectedDislikes, videoId]);
                            } else {
                              setSelectedDislikes(selectedDislikes.filter(id => id !== videoId));
                            }
                          }}
                          className="w-4 h-4 rounded border-gray-500 accent-blue-600 flex-shrink-0"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm text-white truncate">
                            {video?.title || video?.filename || videoId}
                          </div>
                          {video?.group && (
                            <div className="text-xs text-gray-400 truncate">{video.group}</div>
                          )}
                        </div>
                        <button
                          onClick={async () => {
                            try {
                              const response = await fetch(`/api/dislikes/${videoId}`, { method: 'DELETE' });
                              if (response.ok) {
                                const data = await response.json();
                                if (data.success) {
                                  setDislikes(data.dislikes);
                                  setSelectedDislikes(selectedDislikes.filter(id => id !== videoId));
                                }
                              }
                            } catch (e) {
                              console.error('移除不喜欢失败:', e);
                            }
                          }}
                          className="text-red-400 hover:text-red-300 p-1 rounded hover:bg-red-500/20 transition-colors flex-shrink-0"
                          title="移除此项"
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
              
              {selectedDislikes.length > 0 && (
                <div className="flex items-center gap-3">
                  <button
                    onClick={async () => {
                      try {
                        const response = await fetch('/api/dislikes/batch-remove', {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify({ videoIds: selectedDislikes })
                        });
                        if (response.ok) {
                          const data = await response.json();
                          if (data.success) {
                            setDislikes(data.dislikes);
                            setSelectedDislikes([]);
                            alert(`已移除 ${selectedDislikes.length} 个作品的不喜欢标记`);
                          }
                        }
                      } catch (e) {
                        console.error('批量移除失败:', e);
                      }
                    }}
                    className="px-4 py-2 bg-red-600 hover:bg-red-700 rounded-lg text-white text-sm transition-colors"
                  >
                    移除选中项 ({selectedDislikes.length} 个)
                  </button>
                  <button
                    onClick={() => {
                      setSelectedDislikes([]);
                    }}
                    className="px-4 py-2 bg-gray-600 hover:bg-gray-700 rounded-lg text-white text-sm transition-colors"
                  >
                    取消选择
                  </button>
                </div>
              )}
            </>
          )}
        </div>
        
        {/* 搜索和筛选区域 */}
        <div className="bg-gray-800/50 backdrop-blur-sm rounded-lg p-3 md:p-4 mb-4 md:mb-6 border border-gray-700/50">
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-4 md:gap-4">
            {/* 搜索框 */}
            <div className="relative">
              <input
                type="text"
                placeholder="搜索标题、描述或标签..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-3 py-2 md:px-4 md:py-2 bg-gray-700/80 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm md:text-base"
              />
              <div className="absolute right-3 top-2.5 text-gray-400">
                🔍
              </div>
            </div>
            
            {/* 分组筛选 */}
            <div className="flex gap-2">
              <select
                value={filterGroup}
                onChange={(e) => setFilterGroup(e.target.value)}
                className="px-3 py-2 md:px-4 md:py-2 bg-gray-700/80 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500 flex-1 text-sm md:text-base"
              >
                <option value="all">所有分组</option>
                {getAllGroups().map(group => (
                  <option key={group} value={group}>{group}</option>
                ))}
              </select>
              <button
                onClick={() => {
                  const newGroup = prompt('请输入新分组名称:');
                  if (newGroup && newGroup.trim()) {
                    // 创建新分组（通过将选中的视频移动到新分组）
                    if (selectedVideos.length > 0) {
                      handleBatchMoveToGroup(newGroup.trim());
                    } else {
                      alert('请先选择要移动到新分组的视频');
                    }
                  }
                }}
                className="px-2 py-2 md:px-3 bg-purple-600 hover:bg-purple-700 rounded-lg text-white text-xs md:text-sm transition-colors"
                title="创建新分组"
              >
                ➕
              </button>
            </div>
            
            {/* 排序选项 */}
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="px-3 py-2 md:px-4 md:py-2 bg-gray-700/80 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm md:text-base"
            >
              <option value="uploadDate">按上传时间</option>
              <option value="title">按标题</option>
              <option value="viewCount">按观看次数</option>
              <option value="completionRate">按完播率</option>
              <option value="size">按文件大小</option>
            </select>
            
            {/* 批量操作 */}
            {selectedVideos.length > 0 && (
              <div className="flex flex-wrap gap-1 md:gap-2">
                <button
                  onClick={() => handleBatchFavorite(true)}
                  className="px-2 py-1.5 md:px-3 md:py-2 bg-yellow-600 hover:bg-yellow-700 rounded text-white text-xs md:text-sm transition-colors whitespace-nowrap"
                >
                  ❤️ 收藏
                </button>
                <button
                  onClick={() => handleBatchFavorite(false)}
                  className="px-2 py-1.5 md:px-3 md:py-2 bg-gray-600 hover:bg-gray-700 rounded text-white text-xs md:text-sm transition-colors whitespace-nowrap"
                >
                  ❌ 取消
                </button>
                <button
                  onClick={handleBatchDelete}
                  className="px-2 py-1.5 md:px-3 md:py-2 bg-red-600 hover:bg-red-700 rounded text-white text-xs md:text-sm transition-colors whitespace-nowrap"
                >
                  🗑️ 删除
                </button>
                <button
                  onClick={() => {
                    setShowBatchEditModal(true);
                    setBatchEditType('tags');
                    setBatchEditValue('');
                  }}
                  className="px-2 py-1.5 md:px-3 md:py-2 bg-blue-600 hover:bg-blue-700 rounded text-white text-xs md:text-sm transition-colors whitespace-nowrap"
                >
                  🏷️ 编辑
                </button>
              </div>
            )}
          </div>
        </div>

        {/* 视频预览模态框 */}
        {viewingVideo && (
          <div 
            className="fixed inset-0 bg-black/90 flex items-center justify-center z-50 p-4"
            onClick={() => {
              setViewingVideo(null);
              // 退出全屏模式
              if (document.fullscreenElement) {
                document.exitFullscreen().catch(err => {
                  console.error('退出全屏失败:', err);
                });
              }
            }}
          >
            <div 
              className="relative max-w-5xl w-full max-h-[90vh] bg-gray-900 rounded-lg overflow-hidden flex flex-col"
              onClick={(e) => e.stopPropagation()}
            >
              {/* 顶部固定工具栏 */}
              <div className="flex items-center justify-between px-4 py-3 bg-gray-800/90 border-b border-gray-700 flex-shrink-0">
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => {
                      setViewingVideo(null);
                      // 退出全屏模式
                      if (document.fullscreenElement) {
                        document.exitFullscreen().catch(err => {
                          console.error('退出全屏失败:', err);
                        });
                      }
                    }}
                    className="text-white hover:text-gray-300 p-2 rounded-lg bg-black/30 hover:bg-black/50 transition-all flex items-center gap-2"
                    title="关闭"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                    <span className="text-sm hidden sm:inline">关闭</span>
                  </button>
                </div>
                
                <h3 className="text-white font-medium truncate max-w-[200px] sm:max-w-[400px] px-4">
                  {viewingVideo.title || '视频预览'}
                </h3>
                
                <div className="flex items-center gap-2">
                  {/* 编辑按钮 */}
                  <button
                    onClick={() => {
                      setEditingVideo(viewingVideo);
                      setEditFormData({
                        title: viewingVideo?.title || '',
                        description: viewingVideo?.description || '',
                        tags: viewingVideo?.tags?.join(', ') || '',
                        group: viewingVideo?.group || '默认分组'
                      });
                      setViewingVideo(null);
                    }}
                    className="text-white hover:text-blue-400 p-2 rounded-lg bg-black/30 hover:bg-black/50 transition-all flex items-center gap-2"
                    title="编辑"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                    <span className="text-sm hidden sm:inline">编辑</span>
                  </button>
                  
                  {/* 删除按钮 */}
                  <button
                    onClick={() => {
                      handleDeleteVideo(viewingVideo.id);
                      setViewingVideo(null);
                    }}
                    className="text-white hover:text-red-400 p-2 rounded-lg bg-black/30 hover:bg-black/50 transition-all flex items-center gap-2"
                    title="删除"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                    <span className="text-sm hidden sm:inline">删除</span>
                  </button>
                </div>
              </div>
              
              {/* 可滚动内容区域 */}
              <div className="flex-1 overflow-y-auto">
                {/* 视频播放器 */}
                <div className="bg-black flex items-center justify-center">
                  <video
                    src={viewingVideo.url}
                    className="w-full max-h-[60vh] object-contain"
                    controls
                    autoPlay
                    loop
                  />
                </div>
                
                {/* 视频信息 */}
                <div className="p-4 sm:p-6">
                  <h3 className="text-xl font-semibold text-white mb-3">
                    {viewingVideo.title || viewingVideo.description || '抖音视频'}
                  </h3>
                  {viewingVideo.author && (
                    <p className="text-sm text-gray-400 mb-3">
                      👤 作者: {viewingVideo.author}
                    </p>
                  )}
                  {viewingVideo.description && (
                    <p className="text-sm text-gray-300 mb-3 whitespace-pre-wrap">
                      {viewingVideo.description}
                    </p>
                  )}
                  {viewingVideo.tags && viewingVideo.tags.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-4">
                      {viewingVideo.tags.map((tag, idx) => (
                        <span key={idx} className="text-sm bg-blue-600/50 text-blue-200 px-3 py-1 rounded-full">
                          #{tag}
                        </span>
                      ))}
                    </div>
                  )}
                  <div className="flex items-center justify-between text-sm text-gray-400 pt-4 border-t border-gray-700">
                    <div className="flex items-center gap-4">
                      <span>📅 {viewingVideo.uploadDate ? new Date(viewingVideo.uploadDate).toLocaleDateString('zh-CN') : ''}</span>
                      <span>💾 {(viewingVideo.size / 1024 / 1024).toFixed(2)} MB</span>
                    </div>
                    {viewingVideo.originalUrl && (
                      <a 
                        href={viewingVideo.originalUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-400 hover:text-blue-300 underline"
                      >
                        查看原始链接 →
                      </a>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* 视频作品网格 */}
        <div className="bg-gray-800/30 backdrop-blur-sm rounded-lg p-3 md:p-4 border border-gray-700/50">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
            <h2 className="text-lg md:text-xl font-semibold">🎬 作品列表 ({filteredVideos.length} 个)</h2>
            <div className="flex items-center gap-3">
              {selectedVideos.length > 0 && (
                <div className="text-xs md:text-sm text-blue-400">
                  已选择 {selectedVideos.length} 个视频
                </div>
              )}
              <button
                onClick={() => setShowVideoList(v => !v)}
                className="px-3 py-1.5 rounded bg-blue-600 hover:bg-blue-500 text-white text-sm transition-colors"
              >
                {showVideoList ? '收起列表' : '展开列表'}
              </button>
            </div>
          </div>
          
          {showVideoList && (
            <div className="mt-3 md:mt-4">
              {filteredVideos.length === 0 ? (
                <div className="text-center py-12 text-gray-400">
                  <div className="text-4xl mb-4">📁</div>
                  <p className="text-lg">暂无匹配的作品</p>
                  <p className="text-sm mt-2">尝试调整搜索条件或上传新视频</p>
                </div>
              ) : (
                <div className="max-h-[calc(100vh-400px)] overflow-y-auto pb-4">
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3">
                    {paginatedVideos.map((video: VideoItem) => (
                  <div 
                    key={video.id} 
                    className={`group relative bg-gray-800/50 rounded-lg overflow-hidden cursor-pointer transition-all duration-200 hover:scale-[1.02] hover:shadow-lg admin-panel-touch-target ${selectedVideos.includes(video.id) ? 'ring-2 ring-blue-500' : ''}`}
                    onClick={(e) => {
                      if (e.ctrlKey || e.metaKey || e.shiftKey) {
                        // 多选（支持Shift键连续选择）
                        if (selectedVideos.includes(video.id)) {
                          setSelectedVideos(selectedVideos.filter(id => id !== video.id));
                        } else {
                          setSelectedVideos([...selectedVideos, video.id]);
                        }
                      } else {
                        // 单击预览
                        setViewingVideo(video);
                        // 自动进入全屏模式
                        if (!document.fullscreenElement) {
                          document.documentElement.requestFullscreen().catch(err => {
                            console.error('自动全屏失败:', err);
                          });
                        }
                      }
                    }}
                    onTouchStart={(e) => {
                      // 移动端长按多选
                      const touchTimer = setTimeout(() => {
                        if (selectedVideos.includes(video.id)) {
                          setSelectedVideos(selectedVideos.filter(id => id !== video.id));
                        } else {
                          setSelectedVideos([...selectedVideos, video.id]);
                        }
                      }, 500);
                      // 存储定时器以便在touchEnd时清除
                      (e.target as any).touchTimer = touchTimer;
                    }}
                    onTouchEnd={(e) => {
                      // 清除长按定时器
                      if ((e.target as any).touchTimer) {
                        clearTimeout((e.target as any).touchTimer);
                      }
                    }}
                  >
                    {/* 批量选择复选框 */}
                    <div className="absolute top-2 right-2 z-10">
                      <div className="w-6 h-6 rounded-full bg-black/70 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                        {selectedVideos.includes(video.id) ? (
                          <div className="w-4 h-4 bg-blue-500 rounded-full flex items-center justify-center">
                            <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                          </div>
                        ) : (
                          <div className="w-4 h-4 border-2 border-gray-400 rounded-full"></div>
                        )}
                      </div>
                    </div>
                    
                    {/* 视频缩略图 */}
                    <div className="aspect-video bg-black relative overflow-hidden">
                      <LazyThumbnail 
                        videoUrl={video.url} 
                        videoId={video.id}
                        serverThumbnailUrl={video.thumbnailUrl}
                      />
                      
                      {/* 收藏和观看图标 */}
                      <div className="absolute bottom-2 left-2 right-2 flex justify-between text-white text-xs bg-black/50 px-2 py-1 rounded">
                        <span className="flex items-center gap-1">
                          👁️ {(video.viewCount || 0).toLocaleString()}
                        </span>
                        <span className="flex items-center gap-1">
                          {video.isFavorite ? '❤️' : '🤍'}
                        </span>
                      </div>
                      
                      {/* 完播率指示器 */}
                      {video.watchTimeStats?.completionRate && video.watchTimeStats.completionRate > 0 && (
                        <div className="absolute top-2 left-2 bg-green-600/80 text-white text-xs px-2 py-1 rounded">
                          {video.watchTimeStats.completionRate}%
                        </div>
                      )}
                    </div>
                    
                    {/* 视频信息 */}
                    <div className="p-2">
                      <div className="text-xs text-white font-medium line-clamp-2 mb-1">
                        {video.title || video.description?.substring(0, 30) || '未命名视频'}
                      </div>
                      <div className="flex justify-between text-xs text-gray-400">
                        <span>{video.group}</span>
                        <span>{(video.size / 1024 / 1024).toFixed(1)}MB</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              
              {/* 分页控件 */}
              {totalPages > 1 && (
                <div className="flex items-center justify-center gap-2 mt-4 pt-4 border-t border-gray-700/50">
                  <button
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="px-3 py-1.5 rounded bg-gray-700 text-white text-sm disabled:opacity-40 disabled:cursor-not-allowed active:bg-gray-600"
                  >
                    上一页
                  </button>
                  <span className="text-gray-400 text-sm">
                    {currentPage} / {totalPages}
                  </span>
                  <button
                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                    className="px-3 py-1.5 rounded bg-gray-700 text-white text-sm disabled:opacity-40 disabled:cursor-not-allowed active:bg-gray-600"
                  >
                    下一页
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>

        {/* 编辑模态框 */}
        {editingVideo && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
            <div className="bg-gray-800 rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-xl font-bold text-white">编辑作品</h3>
                  <button 
                    onClick={() => setEditingVideo(null)}
                    className="text-gray-400 hover:text-white text-2xl"
                  >
                    ×
                  </button>
                </div>
                
                <div className="space-y-4">
                  {/* 标题编辑 */}
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">标题</label>
                    <input
                      type="text"
                      value={editFormData.title}
                      onChange={(e) => setEditFormData({ ...editFormData, title: e.target.value })}
                      className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white"
                      placeholder="输入作品标题"
                    />
                  </div>
                  
                  {/* 作者显示（只读） */}
                  {(editingVideo.author || editingVideo.authorId) && (
                    <div>
                      <label className="block text-sm text-gray-400 mb-1">作者</label>
                      <div className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded text-gray-400">
                        {editingVideo.author}
                        {editingVideo.authorId && (
                          <span className="text-gray-500 ml-2">(@{editingVideo.authorId})</span>
                        )}
                      </div>
                    </div>
                  )}
                  
                  {/* 描述编辑 */}
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">描述</label>
                    <textarea
                      value={editFormData.description}
                      onChange={(e) => setEditFormData({ ...editFormData, description: e.target.value })}
                      className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white"
                      rows={3}
                      placeholder="作品描述..."
                    />
                  </div>
                  
                  {/* 标签编辑 */}
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">标签（用逗号分隔）</label>
                    <input
                      type="text"
                      value={editFormData.tags}
                      onChange={(e) => setEditFormData({ ...editFormData, tags: e.target.value })}
                      className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white"
                      placeholder="输入标签，如：搞笑,美食,旅行"
                    />
                  </div>
                  
                  {/* 分组编辑 */}
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">分组</label>
                    <input
                      type="text"
                      value={editFormData.group}
                      onChange={(e) => setEditFormData({ ...editFormData, group: e.target.value })}
                      className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white"
                      placeholder="输入分组名称"
                    />
                  </div>
                  
                  {/* 原始链接显示（只读） */}
                  {editingVideo.originalUrl && (
                    <div>
                      <label className="block text-sm text-gray-400 mb-1">原始链接</label>
                      <a 
                        href={editingVideo.originalUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded text-blue-400 hover:text-blue-300 truncate"
                        title={editingVideo.originalUrl}
                      >
                        {editingVideo.originalUrl}
                      </a>
                    </div>
                  )}
                  
                  <div className="flex space-x-3 pt-4">
                    <button
                      onClick={() => setEditingVideo(null)}
                      className="flex-1 bg-gray-600 hover:bg-gray-700 py-2 rounded text-white"
                    >
                      取消
                    </button>
                    <button
                      onClick={handleSaveEdit}
                      className="flex-1 bg-blue-600 hover:bg-blue-700 py-2 rounded text-white"
                    >
                      保存
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
        
        {/* 批量编辑模态框 */}
        {showBatchEditModal && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
            <div className="bg-gray-800 rounded-xl max-w-md w-full max-h-[90vh] overflow-y-auto border border-gray-700/50">
              <div className="p-6">
                <div className="flex justify-between items-center mb-6">
                  <h3 className="text-2xl font-bold text-white">🏷️ 批量编辑</h3>
                  <button 
                    onClick={() => setShowBatchEditModal(false)}
                    className="text-gray-400 hover:text-white text-2xl"
                  >
                    ×
                  </button>
                </div>
                        
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm text-gray-400 mb-2">编辑类型</label>
                    <select
                      value={batchEditType}
                      onChange={(e) => setBatchEditType(e.target.value as any)}
                      className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white"
                    >
                      <option value="tags">标签</option>
                      <option value="title">标题前缀</option>
                      <option value="description">描述</option>
                    </select>
                  </div>
                          
                  <div>
                    <label className="block text-sm text-gray-400 mb-2">
                      {batchEditType === 'tags' ? '标签（用逗号分隔）' :
                       batchEditType === 'title' ? '标题前缀' : '描述内容'}
                    </label>
                    <textarea
                      value={batchEditValue}
                      onChange={(e) => setBatchEditValue(e.target.value)}
                      className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white"
                      rows={3}
                      placeholder={
                        batchEditType === 'tags' ? '输入标签，如：搞笑,美食,旅行' :
                        batchEditType === 'title' ? '输入标题前缀' : '输入描述内容'
                      }
                    />
                  </div>
                          
                  <div className="flex space-x-3 pt-4">
                    <button
                      onClick={() => setShowBatchEditModal(false)}
                      className="flex-1 bg-gray-600 hover:bg-gray-700 py-2 rounded text-white"
                    >
                      取消
                    </button>
                    <button
                      onClick={() => {
                        if (batchEditType === 'tags') {
                          handleBatchAddTags(batchEditValue);
                        } else if (batchEditType === 'title') {
                          handleBatchEditTitle(batchEditValue);
                        } else if (batchEditType === 'description') {
                          handleBatchEditDescription(batchEditValue);
                        }
                        setShowBatchEditModal(false);
                      }}
                      className="flex-1 bg-blue-600 hover:bg-blue-700 py-2 rounded text-white"
                      disabled={!batchEditValue.trim()}
                    >
                      应用
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
        
        {/* 一键自动分组模态框 */}
        {showAutoGroupModal && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
            <div className="bg-gray-800 rounded-xl max-w-md w-full max-h-[90vh] overflow-y-auto border border-gray-700/50">
              <div className="p-6">
                <div className="flex justify-between items-center mb-6">
                  <h3 className="text-2xl font-bold text-white">🤖 一键自动分组</h3>
                  <button 
                    onClick={() => setShowAutoGroupModal(false)}
                    className="text-gray-400 hover:text-white text-2xl"
                    disabled={isAutoGrouping}
                  >
                    ×
                  </button>
                </div>
                        
                <div className="space-y-4">
                  <p className="text-sm text-gray-400">
                    {selectedVideos.length > 0 
                      ? `将对选中的 ${selectedVideos.length} 个视频进行自动分组`
                      : '将对所有视频进行自动分组（未选中任何视频）'}
                  </p>
                          
                  <div>
                    <label className="block text-sm text-gray-400 mb-2">分组策略</label>
                    <select
                      value={autoGroupStrategy}
                      onChange={(e) => setAutoGroupStrategy(e.target.value as any)}
                      className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white"
                    >
                      <option value="smart">🧠 智能标签分组（根据标题/标签/描述自动归类）</option>
                      <option value="time">📅 按上传时间分组（按年月归类）</option>
                      <option value="source">🔗 按来源分组（抖音采集 vs 本地上传）</option>
                      <option value="size">📦 按文件大小分组（小/中/大/超大）</option>
                    </select>
                  </div>
                          
                  <div className="bg-gray-700/50 rounded-lg p-3 text-sm text-gray-400">
                    {autoGroupStrategy === 'smart' && (
                      <span>根据视频标题、描述、标签和文件名中的关键词，智能匹配到对应分组（美食、旅行、萌宠、搞笑等）。无法识别的视频将按来源分为"抖音采集"或"本地上传"。</span>
                    )}
                    {autoGroupStrategy === 'time' && (
                      <span>按照视频的上传时间（年月）进行分组，例如"2024年1月"、"2024年2月"等。</span>
                    )}
                    {autoGroupStrategy === 'source' && (
                      <span>根据视频是否有原始抖音链接进行分组，分为"抖音采集"和"本地上传"两类。</span>
                    )}
                    {autoGroupStrategy === 'size' && (
                      <span>按照视频文件大小进行分组：小文件(&lt;10MB)、中文件(10-50MB)、大文件(50-100MB)、超大文件(&gt;100MB)。</span>
                    )}
                  </div>
                          
                  <div className="flex space-x-3 pt-4">
                    <button
                      onClick={() => setShowAutoGroupModal(false)}
                      className="flex-1 bg-gray-600 hover:bg-gray-700 py-2 rounded text-white"
                      disabled={isAutoGrouping}
                    >
                      取消
                    </button>
                    <button
                      onClick={handleAutoGroup}
                      className="flex-1 bg-gradient-to-r from-cyan-600 to-teal-600 hover:from-cyan-700 hover:to-teal-700 py-2 rounded text-white font-medium"
                      disabled={isAutoGrouping}
                    >
                      {isAutoGrouping ? '⏳ 处理中...' : '✨ 开始自动分组'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
                
        {/* 上传模态框 */}
        {showUploadModal && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
            <div className="bg-gray-800 rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto border border-gray-700/50">
              <div className="p-6">
                <div className="flex justify-between items-center mb-6">
                  <h3 className="text-2xl font-bold text-white">📤 批量上传视频</h3>
                  <button 
                    onClick={() => setShowUploadModal(false)}
                    className="text-gray-400 hover:text-white text-2xl"
                  >
                    ×
                  </button>
                </div>
                
                <div className="space-y-6">
                  <div className="border-2 border-dashed border-gray-600 rounded-xl p-8 text-center hover:border-green-500 transition-colors">
                    <div className="text-4xl mb-4">📁</div>
                    <p className="text-gray-300 mb-4">拖拽视频文件到此处或点击选择文件</p>
                    <input
                      type="file"
                      multiple
                      accept="video/*"
                      onChange={handleFileUpload}
                      className="hidden"
                      id="file-upload"
                    />
                    <label 
                      htmlFor="file-upload" 
                      className="inline-block bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 px-6 py-3 rounded-lg font-medium text-white cursor-pointer transition-all duration-200"
                    >
                      选择视频文件
                    </label>
                    <p className="text-xs text-gray-500 mt-2">支持 MP4, MOV, AVI, MKV, WEBM 格式，单个文件最大 100MB</p>
                  </div>
                  
                  {isUploading && (
                    <div className="bg-gray-700/50 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-white">上传中...</span>
                        <span className="text-gray-400">{uploadProgress}%</span>
                      </div>
                      <div className="w-full bg-gray-600 rounded-full h-2">
                        <div 
                          className="bg-green-500 h-2 rounded-full transition-all duration-300" 
                          style={{ width: `${uploadProgress}%` }}
                        ></div>
                      </div>
                    </div>
                  )}
                  
                  {error && (
                    <div className="bg-red-900/50 border border-red-700 rounded-lg p-4 text-red-300">
                      {error}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
        
        {/* 导航链接 */}
        <div className="flex flex-wrap justify-center gap-4 mt-8">
          <a 
            href="/admin/analytics" 
            className="inline-block bg-orange-600 hover:bg-orange-700 px-6 py-3 rounded-lg font-medium transition-colors"
          >
            📊 统计分析
          </a>
          <a 
            href="/admin/collection" 
            className="inline-block bg-blue-600 hover:bg-blue-700 px-6 py-3 rounded-lg font-medium transition-colors"
          >
            📥 视频采集
          </a>
          <a 
            href="/chat" 
            className="inline-block bg-indigo-600 hover:bg-indigo-700 px-6 py-3 rounded-lg font-medium transition-colors"
          >
            💬 聊天助手
          </a>
          <a 
            href="/admin/api-settings" 
            className="inline-block bg-purple-600 hover:bg-purple-700 px-6 py-3 rounded-lg font-medium transition-colors"
          >
            ⚙️ API设置
          </a>
          <a 
            href="/admin/hls"
            className="inline-block bg-orange-600 hover:bg-orange-700 px-6 py-3 rounded-lg font-medium transition-colors"
          >
            🎬 HLS分片
          </a>
          <a 
            href="/admin/tools"
            className="inline-block bg-pink-600 hover:bg-pink-700 px-6 py-3 rounded-lg font-medium transition-colors"
          >
            🔧 管理工具箱
          </a>
          <a 
            href="/" 
            className="inline-block bg-green-600 hover:bg-green-700 px-6 py-3 rounded-lg font-medium transition-colors"
          >
            ▶️ 返回播放器
          </a>
        </div>
      </div>

      {/* 批量导入模态框 */}
      {showBatchImportModal && (
      <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
        <div className="bg-gray-800 rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto border border-gray-700/50">
          <div className="p-6">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-2xl font-bold text-white">📦 批量导入视频</h3>
              <button 
                onClick={() => {
                  setShowBatchImportModal(false);
                  setImportResult(null);
                }}
                className="text-gray-400 hover:text-white text-2xl"
              >
                ×
              </button>
            </div>
            
            <div className="space-y-4">
              {/* 导入目录 */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  导入目录路径
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={importDir}
                    onChange={(e) => setImportDir(e.target.value)}
                    placeholder="例如: C:/Videos/Import 或 /home/user/import"
                    className="flex-1 px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                  <button
                    onClick={() => {
                      // 尝试使用系统对话框（如果支持）
                      const input = document.createElement('input');
                      input.type = 'file';
                      (input as any).webkitdirectory = true;
                      input.onchange = (e: any) => {
                        const files = e.target.files;
                        if (files && files.length > 0) {
                          // 获取根目录路径
                          const fullPath = files[0].webkitRelativePath || files[0].name;
                          const dirPath = fullPath.split('/')[0];
                          setImportDir(dirPath);
                        }
                      };
                      input.click();
                    }}
                    className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 rounded-lg text-white font-medium transition-colors whitespace-nowrap flex items-center gap-2"
                    title="选择文件夹"
                  >
                    📁 选择
                  </button>
                </div>
                
                {/* 元数据模式选择 */}
                <div className="mt-3 flex gap-3">
                  <button
                    onClick={() => setImportMetadataMode('separate')}
                    className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-all border ${
                      importMetadataMode === 'separate'
                        ? 'bg-indigo-600 border-indigo-500 text-white'
                        : 'bg-gray-700 border-gray-600 text-gray-400 hover:border-gray-500'
                    }`}
                  >
                    📂 分离模式
                    <div className="text-xs mt-1 opacity-80">
                      videos/ + metadata/ 子目录
                    </div>
                  </button>
                  <button
                    onClick={() => setImportMetadataMode('together')}
                    className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-all border ${
                      importMetadataMode === 'together'
                        ? 'bg-purple-600 border-purple-500 text-white'
                        : 'bg-gray-700 border-gray-600 text-gray-400 hover:border-gray-500'
                    }`}
                  >
                    📁 混合模式
                    <div className="text-xs mt-1 opacity-80">
                      视频和JSON在同一目录
                    </div>
                  </button>
                </div>
                
                <p className="text-xs text-gray-400 mt-2">
                  {importMetadataMode === 'separate' ? (
                    <>
                      <strong>分离模式：</strong>
                      <br />• videos/ - 视频文件目录
                      <br />• metadata/ - 元数据JSON文件目录（可选）
                    </>
                  ) : (
                    <>
                      <strong>混合模式：</strong>
                      <br />• 所有视频文件和对应的.json元数据在同一目录
                      <br />• 文件名匹配：video.mp4 ↔ video.json
                    </>
                  )}
                </p>
              </div>

              {/* 默认分组 */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  默认分组名称
                </label>
                <input
                  type="text"
                  value={importDefaultGroup}
                  onChange={(e) => setImportDefaultGroup(e.target.value)}
                  placeholder="默认分组"
                  className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>

              {/* 选项 */}
              <div className="space-y-3 bg-gray-700/30 p-4 rounded-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-sm font-medium text-white">自动提取标签</div>
                    <div className="text-xs text-gray-400">从文件名中自动提取关键词作为标签</div>
                  </div>
                  <button
                    onClick={() => setImportAutoTag(!importAutoTag)}
                    className={`relative w-12 h-6 rounded-full transition-colors duration-200 ${
                      importAutoTag ? 'bg-green-500' : 'bg-gray-600'
                    }`}
                  >
                    <div
                      className={`absolute top-0.5 w-5 h-5 bg-white rounded-full transition-transform duration-200 ${
                        importAutoTag ? 'translate-x-6' : 'translate-x-0.5'
                      }`}
                    />
                  </button>
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-sm font-medium text-white">跳过重复文件</div>
                    <div className="text-xs text-gray-400">如果视频已存在则跳过</div>
                  </div>
                  <button
                    onClick={() => setImportSkipDuplicates(!importSkipDuplicates)}
                    className={`relative w-12 h-6 rounded-full transition-colors duration-200 ${
                      importSkipDuplicates ? 'bg-green-500' : 'bg-gray-600'
                    }`}
                  >
                    <div
                      className={`absolute top-0.5 w-5 h-5 bg-white rounded-full transition-transform duration-200 ${
                        importSkipDuplicates ? 'translate-x-6' : 'translate-x-0.5'
                      }`}
                    />
                  </button>
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-sm font-medium text-white">校验视频有效性</div>
                    <div className="text-xs text-gray-400">导入前检查视频编码和格式（需要 FFmpeg）</div>
                  </div>
                  <button
                    onClick={() => setImportValidateVideos(!importValidateVideos)}
                    className={`relative w-12 h-6 rounded-full transition-colors duration-200 ${
                      importValidateVideos ? 'bg-green-500' : 'bg-gray-600'
                    }`}
                  >
                    <div
                      className={`absolute top-0.5 w-5 h-5 bg-white rounded-full transition-transform duration-200 ${
                        importValidateVideos ? 'translate-x-6' : 'translate-x-0.5'
                      }`}
                    />
                  </button>
                </div>
              </div>

              {/* 导入结果 */}
              {importResult && (
                <div className="bg-gray-700/30 p-4 rounded-lg">
                  <h4 className="text-sm font-medium text-white mb-2">导入结果</h4>
                  <div className="grid grid-cols-4 gap-2 text-center">
                    <div className="bg-gray-600/50 rounded p-2">
                      <div className="text-2xl font-bold text-white">{importResult.summary.total}</div>
                      <div className="text-xs text-gray-400">总计</div>
                    </div>
                    <div className="bg-green-600/50 rounded p-2">
                      <div className="text-2xl font-bold text-green-400">{importResult.summary.imported}</div>
                      <div className="text-xs text-gray-400">成功</div>
                    </div>
                    <div className="bg-yellow-600/50 rounded p-2">
                      <div className="text-2xl font-bold text-yellow-400">{importResult.summary.skipped}</div>
                      <div className="text-xs text-gray-400">跳过</div>
                    </div>
                    <div className="bg-red-600/50 rounded p-2">
                      <div className="text-2xl font-bold text-red-400">{importResult.summary.failed}</div>
                      <div className="text-xs text-gray-400">失败</div>
                    </div>
                  </div>
                </div>
              )}

              {/* 操作按钮 */}
              <div className="flex gap-3 pt-4">
                <button
                  onClick={() => {
                    setShowBatchImportModal(false);
                    setImportResult(null);
                  }}
                  disabled={isImporting}
                  className="flex-1 px-4 py-2 bg-gray-600 hover:bg-gray-700 rounded-lg text-white font-medium transition-colors disabled:opacity-50"
                >
                  取消
                </button>
                <button
                  onClick={handleBatchImport}
                  disabled={isImporting || !importDir}
                  className="flex-1 px-4 py-2 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 rounded-lg text-white font-medium transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {isImporting ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                      导入中...
                    </>
                  ) : (
                    '开始导入'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      )}
    </div>
  );
};

export default AdminPanel;